import { useMutation } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { logger } from "@/core/utils/func/logger";
import { authEndpoints } from "@/features/auth/api/endpoints";
import type { ChangeMyPasswordRequest } from "@/features/auth/api/dto";

/**
 * Change le mot de passe de l'utilisateur **connecté** (`POST /Users/me/password`,
 * guide v4.1 §6.9). AUTHENTIFIÉ. Le compte visé est celui du jeton (`sub`) :
 * aucun identifiant n'est accepté dans le corps. Le mot de passe actuel est
 * vérifié par une **authentification Keycloak réelle** (protection
 * brute-force du realm applicable) : un jeton dérobé ne suffit pas.
 *
 * Codes serveur :
 * - 204 succès — toutes les sessions sont fermées, l'application se
 *   reconnecte via `useLogin` avec le nouveau mot de passe ;
 * - 422 mot de passe actuel incorrect (volontairement 422 et non 401, pour ne
 *   pas être pris pour un jeton expiré) ; compte temporairement verrouillé ;
 *   ou nouveau mot de passe identique à l'actuel ;
 * - 400 nouveau mot de passe refusé par la politique du realm ;
 * - 503 Keycloak indisponible.
 */
export const useChangeMyPassword = () =>
  useMutation({
    mutationKey: ["auth", "users", "changeMyPassword"],
    mutationFn: (body: ChangeMyPasswordRequest) =>
      mutateData({
        url: authEndpoints.changeMyPassword,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<void>,
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useChangeMyPassword failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "auth" }
      );
    },
  });
